import { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import axios from "axios";
import { showError } from '../toast/toastUtil';
import config from '../../config';
import { markAuthSession } from '../auth/authConfig';
import {

  handle401Error,
  handleNetworkError,
  showSessionErrorModal,
} from '../auth/sessionErrorHandler';

let showSessionExpiredModal: ((message?: string) => void) | null = null;

export const setSessionExpiredModalHandler = (handler: (message?: string) => void) => {
  showSessionExpiredModal = handler;
};

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(undefined);
    }
  });
  failedQueue = [];
};

const retryRequest = (originalRequest: InternalAxiosRequestConfig) =>
  axios({ ...originalRequest, withCredentials: true });

const isAuthenticationError = (error: AxiosError<{
  code?: string;
  message?: string;
  detailMessage?: string;
}>): boolean => {
  const responseData = error.response?.data as { detailMessage?: string; message?: string } | undefined;
  const message = responseData?.detailMessage
    || responseData?.message
    || error.message
    || '';

  const lowerMessage = message.toLowerCase();

  return lowerMessage.includes('expired jwt token') ||
        lowerMessage.includes('expired') ||
        lowerMessage.includes('token') ||
        lowerMessage.includes('authentication') ||
        lowerMessage.includes('unauthorized') ||
        lowerMessage.includes('full authentication is required') ||
        lowerMessage.includes('invalid token') ||
        lowerMessage.includes('token expired');
};

const isAuthRequiredRequest = (originalRequest: InternalAxiosRequestConfig): boolean => {
  const url = originalRequest?.url || '';

  const noAuthEndpoints = [
    '/auth/signin',
    '/auth/refresh',
    '/auth/signup',
    '/auth/find',
    'business.juso.go.kr',
    '/auth/password/temp'
  ];

  if (noAuthEndpoints.some(endpoint => url.includes(endpoint))) {
    return false;
  }

  return true;
};

const showUnauthorizedMessage = (
  isDev: boolean,
  error: AxiosError<{
    code: string;
    message: string;
    detailMessage: string;
  }>
) => {
  if (isDev) {
    showError(`[DEV] 401 에러 발생 (인증 문제 아님): ${error.response?.data?.message || error.message}`);
  } else {
    showError('접근 권한이 없거나 요청을 처리할 수 없습니다.');
  }
};

const refreshTokens = async () => {
  const response = await axios.post(
    `${config.apiUrl}/auth/refresh`,
    {},
    { withCredentials: true }
  );
  if (!response?.data?.successFlag) {
    throw new Error('토큰 갱신 실패');
  }
  markAuthSession();
};

const handle401ErrorResponse = async (
  error: AxiosError<{
    code: string;
    message: string;
    detailMessage: string;
  }>,
  originalRequest: InternalAxiosRequestConfig & { _retry?: boolean },
  isDev: boolean
) => {
  const isAuthError = isAuthenticationError(error);
  const isAuthRequired = isAuthRequiredRequest(originalRequest);

  if (!isAuthError && !isAuthRequired) {
    showUnauthorizedMessage(isDev, error);
    return Promise.resolve(error.response);
  }

  if (originalRequest._retry || originalRequest.url?.includes('/auth/signin')) {
    handle401Error('api-call', error, false);
    return Promise.resolve(error.response);
  }

  if (originalRequest.url?.includes('/auth/refresh')) {
      handle401Error('token-refresh', error, false);
    return Promise.resolve(error.response);
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then(() => retryRequest(originalRequest))
      .catch(err => Promise.reject(err));
  }

  originalRequest._retry = true;
  isRefreshing = true;

  try {
    await refreshTokens();
    processQueue(null);
    return retryRequest(originalRequest);
  } catch (refreshError: unknown) {
    processQueue(refreshError);
    handle401Error('token-refresh', refreshError as Error, false);
    return Promise.resolve(error.response);
  } finally {
    isRefreshing = false;
  }
};

export const onRequest = (config: InternalAxiosRequestConfig) => {
  if (config.withCredentials !== false) {
    config.withCredentials = true;
  }
  return config;
};

export const onErrorRequest = (error: Error) => {
  return Promise.reject(error);
};

export const onResponse = (response: AxiosResponse) => {
  return response;
};

export const onErrorResponse = async (error: AxiosError<{
  code: string;
  message: string;
  detailMessage: string;
}>) => {
  try {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const isDev = config.appMode === 'dev' || config.runMode === 'dev';

    if (error.response?.status === 401) {
      return handle401ErrorResponse(error, originalRequest, isDev);
    }

    if (isDev) {
      const errorType = !error.response
        ? 'NETWORK/CORS'
        : !error.response.status
        ? 'NO_STATUS'
        : error.response.status;

      const message = error.response?.data?.detailMessage
        || error.response?.data?.message
        || error.message
        || '알 수 없는 오류';

      showError(`[DEV] ${errorType} 에러: ${message}`);

      if (error.response) {
        return Promise.resolve(error.response);
      }

      return Promise.reject(error);
    }

    if (error.response) {
      if (!error.response?.data) {
        console.error('🔴 응답 데이터가 없는 에러:', error);
        showError('서버 응답 오류가 발생했습니다.');
        return Promise.reject(error);
      }

      const responseData = error.response.data;
      const message = responseData.detailMessage || responseData.message || '오류가 발생했습니다.';

      const isServerError = (error.response.status ?? 0) >= 500;
      if (isServerError) {
        const isRefreshPath = originalRequest.url?.includes('/auth/refresh');

        if (isRefreshPath) {
          showSessionErrorModal('서버 오류가 발생했습니다. 다시 로그인해주세요.');
          return Promise.resolve(error.response);
        }
        showError(message, 5000);
      } else {
        showError(message, 5000);
      }

      return Promise.resolve(error.response);
    }

    handleNetworkError('network-error', error);
    return Promise.reject(error);
  } catch (interceptorError: unknown) {
    const isDev = config.appMode === 'dev' || config.runMode === 'dev';
    if (isDev) {
      showError('[DEV] 인터셉터 에러 발생');
      return Promise.reject(interceptorError);
    }

    handleNetworkError('interceptor-error', interceptorError as Error);
    return Promise.reject(interceptorError);
  }
};

const axiosInterceptor = {
  onRequest,
  onErrorRequest,
  onResponse,
  onErrorResponse,
};

export default axiosInterceptor;
